/**
 * serviceLayerClient.body — body-encoding helpers split out of
 * `serviceLayerClient.ts` to keep that file under the 300 LOC cap.
 *
 * Three distinct concerns live here:
 *   - `isMultipartBody`         — true for FormData / Blob uploads
 *   - `encodeBody`              — used by post/put/patch to pass FormData
 *                                  through unchanged and JSON.stringify
 *                                  plain objects
 *   - `bodyForHmacSigning`      — produces the canonical body string the
 *                                  HMAC layer signs (FormData → '' since
 *                                  the browser sets the multipart
 *                                  boundary; the server validates the
 *                                  multipart structure separately)
 *
 * Per codex P1 #1 (PR #1486 r3198260687): every paperclip/drag/paste
 * upload silently failed because `post()` JSON.stringify'd FormData
 * before `serviceFetch` could see it.
 */

export function isMultipartBody(body: unknown): body is FormData | Blob {
  return body instanceof FormData || body instanceof Blob
}

/**
 * post / put / patch helper. Pass-through for FormData/Blob, otherwise
 * JSON.stringify. Plain `null`/`undefined` → `undefined` (no body).
 */
export function encodeBody(body?: unknown): BodyInit | undefined {
  if (body == null) return undefined
  if (isMultipartBody(body)) return body
  return JSON.stringify(body)
}

/**
 * What the HMAC layer signs. JSON path stringifies; multipart path returns
 * empty string (signing the multipart bytes here would race the browser's
 * boundary derivation; the server validates structure separately).
 */
export function bodyForHmacSigning(body: unknown): string {
  if (typeof body === 'string') return body
  if (isMultipartBody(body)) return ''
  if (typeof body === 'object' && body !== null) return JSON.stringify(body)
  return ''
}
